import scrollIntoView from 'scroll-into-view';
import moment from './moment';
import { getValue } from '@mui/system';

export const getWordCount = (value) => value?.length > 0 ? value.replaceAll(/[.,/#!$%^&*;:{}=\-_`~() ]+/g, ' ').trim().split(' ').length : 0;

export const formatDateTime = (value) => value ? moment(value).format('MM/DD/YYYY h:mm A') : 'unknown date';

export const nullishTernary = (value, positive, negative) => {
  if (value === null || value === undefined) {
    return value;
  }
  return value ? positive : negative;
};

export const isEmpty = (value) => {
  return value === null || value === undefined || value === '';
};

/**
 * Get a default value for radio buttons, which may be true, false, or null for no selection.
 */
export const maybeValue = (value) => {
  if (value !== null) {
    return value ? 'true' : 'false';
  }
  return null;
};

export const HTTP_ERRORS = [
  'Internal server error',
  'Bad Request'
];

/**
 * Merges status messages into a flat array.
 *
 * Can accept a string, an error object with 'message' and 'errors' properties, or an array containing a mix
 * of strings and error objects.
 *
 * @param msg
 * @returns {string[]}
 */
export const getArrayMsg = (msg) => {
  if (!msg) {
    return [];
  }

  if (!Array.isArray(msg)) {
    msg = [msg];
  }
  return msg.map(m => {
    if (!!m.errors || !!m.message) {
      // An error object was passed in. Collect strings from the 'message' and 'errors' properties.
      return [m.message, ...m.errors || []];
    }
    return m;
  }).flat().filter(m => !!m && !HTTP_ERRORS.includes(m));
};

export const toOption = (id, label = null) => ({ id, label: label || id });

/**
 * Helper to convert arrays in the form of [{value: 'foo'}] to options usable by <AutocompleteElement>
 */
export const toOptions = (opts) => opts.map(opt => {
  if (typeof opt === 'string') {
    return {
      id: opt,
      label: opt,
    };
  }
  if (opt.value) {
    return {
      id: opt.value,
      label: `${opt.label || opt.value}`,
    };
  }
  return opt;
});

/**
 * Gets the user-friendly label from a list of options
 *
 * examples:
 * ```
 * getOptionLabel(participantNeeds, 'food_access') // returns 'Participant / Community food access'
 * getOptionLabel(eventDemographics.ageRange, 'k-2') // returns 'K-2nd Grade'
 * ```
 *
 * @param opts The array of options
 * @param id The value from the DB
 * @return string
 */
export const getOptionLabel = (opts, id) => {
  const match = opts.find(o => o.id === id || o.code === id);
  if (match) {
    return match.label;
  }
  return id;
};

/**
 * Format's a moment date/time into ISO standard as UTC without the conversion so that the
 * local time is pretended to be UTC all along.
 * @param m
 * @returns {string}
 */
export const notzUTCString = (m) => m.format('YYYY-MM-DDTHH:mm:ss.sss') + 'Z';

export const fullDateFormat = (mom) => mom.format('dddd, MMMM D YYYY | h:mm A');

/**
 * Sort an array of items based on the display_order property.
 * If no display_order is set, then a fallback attribute is used for string comparison.
 * @param items
 * @param fallbackAttr
 * @returns {*}
 */
export const sortDisplayOrder = (items = [], fallbackAttr = null) => {
  items.sort((a, b) => {
    if (a.display_order && b.display_order) {
      return a.display_order - b.display_order;
    } else if (a.display_order) {
      return -1;
    } else if (b.display_order) {
      return 1;
    } else if (fallbackAttr) {
      return a[fallbackAttr].localeCompare(b[fallbackAttr]);
    }
    return a - b;
  });
  return items;
};

/**
 * Sorts a list of options alphabetically by label. Use with array.sort().
 * e.g., `options.sort(sortByLabel)`
 */
export const sortByLabel = (a, b) => {
  if (!a.label || !b.label) {
    return 0;
  }
  return a.label.localeCompare(b.label);
};

/**
 * Sorts a list of options alphabetically by label. Use with array.sort().
 * Puts any options whose labels begin with "Other" at the end.
 * e.g., `options.sort(sortByLabel)`
 */
export const sortByLabelWithOtherLast = (a, b) => {
  if (!a.label || !b.label) {
    return 0;
  }
  const aLabel = a.label.startsWith('Other') ? `zzz${a.label}` : a.label;
  const bLabel = b.label.startsWith('Other') ? `zzz${b.label}` : b.label;
  return aLabel.localeCompare(bLabel);
};

/**
 * Basic object comparator that compares the properties of objects 'a' and 'b' specified by 'orderBy'
 *
 * @param a
 * @param b
 * @param orderBy
 * @returns {number}
 */
export const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

/**
 * Return comparator based on string direction.
 *
 * @param order
 * @param orderBy
 * @returns {{(*, *): number, (*, *): number}}
 */
export const defaultComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

export const scrollToTitle = () => {
  setTimeout(() => {
    let elems = document.getElementsByClassName('page-title');
    if (elems.length > 0) {
      scrollIntoView(elems[0], { align: { top: 0 } });
    } else {
      window.scrollTo({ top: 200, left: 0, behavior: 'smooth' });
    }
  }, 100);
};

export const scrollToAlert = () => {
  setTimeout(() => {
    let root = document.getElementsByClassName('MuiModal-root')?.[0];
    let modal = true;
    if (!root) {
      modal = false;
      root = document;
    }
    /** @type {HTMLInputElement} */
    let e = root.getElementsByClassName('alert-anchor')[0];
    if (e) {
      if (modal) {
        e.scrollIntoView({ behavior: 'smooth' });
      } else {
        scrollIntoView(e, { align: { top: 0 } });
      }
      setTimeout(() => e.focus({ preventScroll: true }), 100);
    } else {
      scrollToTitle();
    }
  }, 100);
};

export const capitalize = (str) => `${str.substring(0, 1).toUpperCase()}${str.substring(1)}`;

export const truncateString = (str, len = 25) => {
  if (!str) {
    return '';
  }
  return str.length > len ? `${str.substring(0, len)}...` : str;
};

export const last = (arr) => arr[arr.length - 1];

export const tenDigit = (phone) => {
  return phone.replace(/\+1/, '').replaceAll(/[^0-9]/g, '');
};

export const paletteMains = (theme) => Object.keys(theme.palette).reduce((acc, key) => {
  acc[key] = theme.palette[key].main;
  return acc;
}, {});

export const preventNonNumericKeyPress = (e) => {
  // <input type="number"> allows some mathematical chars like +-.e which we don't want.
  // This prevents users from entering those chars, while still allowing keys like
  // backspace, arrows, etc. to work.
  ['e', 'E', '+', '-', '.', ' '].includes(e.key) && e.preventDefault();
};

export const validatePositiveInteger = {
  validate: {
    integersOnly: (value) => {
      if (!!value && value !== Math.floor(value)) {
        return 'Please enter a whole number.';
      }
      return true;
    },
  },
  min: {
    value: 0,
    message: 'Please enter a positive number.'
  },
  max: {
    // Huge numbers cause API errors.
    value: 99999,
    message: 'Please enter a number below 100,000.'
  }
};

/**
 * Returns data received from the API in object form. Parses JSON strings, if necessary.
 */
export const maybeParseJson = (value) => {
  if (!value) {
    return {};
  }
  if (typeof value === 'string') {
    return JSON.parse(value);
  }
  return value;
};

export const stringify = (obj, pretty = true) => pretty ? JSON.stringify(obj, null, 2) : JSON.stringify(obj);

export const stringsToOptions = (labels) => labels.map((label) => ({ id: label, label }));

export const setToOptions = (set) => stringsToOptions(Array.from(set));

export function calculateMaxAlertHeight(container, childs) {
  /** @type {HTMLDivElement} */
  const tempContainer = document.createElement('div');
  tempContainer.style.cssText = `
    position: absolute;
    visibility: hidden;
    width: ${container.offsetWidth}px;
    top: -9999px;
  `;

  let maxHeight = 0;

  childs.forEach(content => {
    tempContainer.innerHTML = content;
    document.body.appendChild(tempContainer);
    maxHeight = Math.max(maxHeight, tempContainer.offsetHeight);
    document.body.removeChild(tempContainer);
  });

  return maxHeight;
}

export const asterisk =
  <label
    className="MuiFormControlLabel-root MuiFormControlLabel-labelPlacementEnd Mui-required css-mrydm8-MuiFormControlLabel-root"
    style={{ display: 'inline' }}>
      <span
        aria-hidden="true"
        className="form.mui Mui-required MuiFormControlLabel-asterisk color-red">
        *
      </span>
  </label>;

/**
 * Generates a pluralized phrase with a noun, count, and optional verb conjugation.
 * Formats the output as "[count] [noun](s) [verb]" with proper grammatical agreement.
 * When count is 0, replaces the number with "no". When count is 1, uses singular form.
 * Supports verb conjugation for "is/are" and "was/were". Add more if needed.
 *
 * @param {Object} params - The pluralization parameters
 * @param {string} params.noun - The noun to be pluralized
 * @param {number} params.count - The count that determines singular or plural form
 * @param {boolean} [params.upperfirst=false] - Whether to capitalize the first letter of the result
 * @param {string} [params.verb=''] - Optional verb to conjugate ('is', 'are', 'was', or 'were')
 * @return {string} The formatted pluralized phrase with optional verb conjugation
 */
export function plural({
  noun,
  count,
  upperfirst = false,
  verb = '',
}) {
  const suffix = count === 1 ? '' : 's';
  const prefix = count === 0 ? 'no' : `${count}`;
  let verbTenses = null;
  if (verb) {
    const verbs = [
      ['is', 'are'],
      ['was', 'were'],
    ];
    verbTenses = verbs.find((tenses) => tenses.includes(verb));
  }
  let result = `${prefix} ${noun}${suffix}`;
  if (verbTenses) {
    result = `${result} ${verbTenses[count === 1 ? 0 : 1]}`;
  }
  if (upperfirst) {
    return result[0].toUpperCase() + result.slice(1);
  }
  return result;
}

/**
 * getAnswerLabels retrieves the labels for the provided answers, formatting other_text.
 *
 * @param answers - one question's answers from an event_answers / completed_answers map
 * @returns {*[]}
 */
export function getAnswerLabels(answers) {
  return answers?.map((opt) => {
    if (opt.code === 'other') {
      return `Other: ${opt.other_text}`;
    }
    return opt.label;
  }) ?? [];
}

/**
 * prefillAnswers takes the values from another form and uses them as form values for any slug matching in another form.
 * e.g., Use the answers on a Session object to pre-fill the answers on the Complete Session form.
 * @param formQuestions - The questions available for the current form without form values
 * @param prevAnswers - The answers from the previous/providing form as retrieved from database
 */
export function prefillAnswers(formQuestions = [], prevAnswers = {}) {
  const stubbedAnswers = formQuestions.reduce((acc, q) => {
    acc[q.slug] = [];
    if (q.slug in prevAnswers) {
      acc[q.slug] = prevAnswers[q.slug];
    }
    return acc;
  }, {});
  return getValuesFromAnswers(stubbedAnswers, formQuestions);
}

// Prepare answers for submission to the back end.
// All answers are added to the same array regardless of question since they have unique question_option_ids.
export function prepareAnswers(formQuestions = [], values = {}) {
  return formQuestions.reduce((acc, q) => {
    const slug = q.slug;
    for (const questionOption of q.question_options) {
      let hasOption;
      switch (q.type) {
        case 'SINGLE_SELECT':
          hasOption = (values[slug] ?? null) === questionOption.id;
          break;
        case 'MULTI_SELECT':
          hasOption = (values[slug] ?? []).includes(questionOption.id);
          break;
        default: continue;
      }
      if (hasOption) {
        acc.push({
          question_option_id: questionOption.id,
          other_text: (questionOption.code === 'other' ? values?.[`${slug}_other`] ?? null : null),
        });
      }
    }
    return acc;
  }, []);
}

/**
 * Formats the questions for the form into an object keyed by the question slug. Also filters the options
 * for each question to only include the ones that should be visible.
 *
 * @param {array} formQuestions All questions for the form, with all options, including inactive ones
 * @param {object} existingAnswers All existing answers for the object being edited. Output of getValuesFromAnswers().
 * @return {object} The questions, keyed by slug, with only the visible options.
 */
export function formatFormQuestions(formQuestions, existingAnswers) {
  const questions = {};
  formQuestions.forEach(question => {
    const { question_options, ...questionMeta } = question;

    // The question_options array contains all options, including inactive ones.
    // Here, we filter them to show only the active ones and any that were included in previous answers.
    // e.g., If "SNAP-Ed" is an inactive project, then the following logic applies:
    // 1. Creating a new event: Project list does not show "SNAP-Ed"
    // 2. Editing a SNAP-Ed event: Project list **shows** "SNAP-Ed"
    // 3. Editing an event from a different project: Project list does not show "SNAP-Ed"
    const answer = existingAnswers[questionMeta.slug];
    const visibleOptions = question_options
      .filter(opt => opt.active || (Array.isArray(answer) ? answer.includes(opt.id) : answer === opt.id))
      .map(opt => ({
        id: opt.id,
        label: opt.label,
        custom: opt.custom,
        active: opt.active,

        // Some options (e.g., the "other" ones) will show a text field to enter the "Other" text.
        // This is used by the <AutocompleteOther> component.
        ...(opt.code === 'other' && {
          textFieldLabel: 'Please describe',
          text: opt.other_text ?? true,
          textId: `${questionMeta.slug}_other`,
        }),
      }));

    questions[questionMeta.slug] = {
      ...questionMeta,
      options: visibleOptions
    }
  });

  return questions;
}

/**
 * Convert *_answers received from a query into form field versions.
 * @param questionAnswers - Answers returned from query in *_answers (e.g. completed_answers, event_answers, etc..)
 * @param formQuestions - The questions and question meta for the observed form.
 * @returns {{ [string]: number[] | string[] | number | string}}
 */
export function getValuesFromAnswers(questionAnswers, formQuestions) {
  const slugToType = formQuestions.reduce((acc, { slug, type }) => ({ ...acc, [slug]: type }), {});
  return Object.entries(questionAnswers ?? {}).reduce((acc, [slug, answers]) => {
    // These are spelled out because it didn't feel right to assume that the negative of one is positive of the other.
    const isMulti = slugToType[slug] === 'MULTI_SELECT';
    const isSingle = slugToType[slug] === 'SINGLE_SELECT';
    acc[slug] = isMulti ? [] : null;
    for (const { question_option_id, code, other_text = null } of answers) {
      if (isMulti) {
        acc[slug].push(question_option_id);
      } else if (isSingle) {
        acc[slug] = question_option_id;
      }
      if (code === 'other') {
        acc[`${slug}_other`] = other_text;
      }
    }
    return acc;
  }, {});
}

/**
 * Parse a string (URL param, DOM value) into a number.
 * @returns {number|null} the number, or `fallback` when the value is absent or not numeric
 */
export function parseNumber(value, fallback = null) {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function isOptionEqualToValue(option, newValue) {
  return (option?.id ?? option?.label ?? option) === (newValue?.id ?? newValue?.label ?? newValue);
}

// Site fields carry a label and (since SEMS-2016) an id. Null when neither is set, so an empty
// field stays falsy for `required` and the submit reducers don't send the placeholder object.
export function toSiteOption(id, label) {
  return id || label ? { id: id ?? null, label: label ?? '' } : null;
}
